import "./PluginPresentation.scss";
import {
    ArrowRight,
    Code2,
    Lightbulb,
    Rocket,
    Wrench,
} from "lucide-react";
import PluginPresentationJava from "../../Components/PluginPresentationJava/PluginPresentationJava";
import {useTranslation} from "react-i18next";
import {DiscordMember} from "../../types";
import {discordUrl} from "../../Global";

type Developer = {
    id: string;
    name: string;
    role: string;
    avatar: string;
    url?: string;
};

interface WebPluginProps {
    staff: DiscordMember[] | [];
}


const steps = [
    {
        id: "01",
        icon: Lightbulb,
        title: "Capiamo la tua idea",
        description:
            "Ascoltiamo la tua visione, analizziamo le esigenze del tuo server e definiamo insieme gli obiettivi.",
    },
    {
        id: "02",
        icon: Wrench,
        title: "Progettiamo la soluzione",
        description:
            "Disegniamo l'architettura del progetto, le integrazioni con altri sistemi e l'esperienza utente che farà la differenza.",
    },
    {
        id: "03",
        icon: Code2,
        title: "Sviluppiamo il codice",
        description:
            "Scriviamo codice ottimizzato e performante, seguendo le best practice per garantire stabilità e sicurezza.",
    },
    {
        id: "04",
        icon: Rocket,
        title: "Test & Deploy",
        description:
            "Eseguiamo test approfonditi, ti consegnamo il prodotto e ti supportiamo per i primi 30 giorni con aggiornamenti e fix.",
    },
];

export default function PluginPresentation({ staff }: WebPluginProps) {
    const { t } = useTranslation();
    let pluginDevelopers: DiscordMember[] | [] = [];

    pluginDevelopers = staff.filter((member) =>
        member.roles.some((role) => role.id === "1425534503396315156")
    );

    return (
        <section className="plugin-dev">
            {/* ===== HERO ===== */}

            <div className="plugin-dev__hero">
                <div className="plugin-dev__hero-container">
                    {/* Left */}
                    <div className="plugin-dev__hero-content">
                        <h1 className="plugin-dev__title">
                            {t("plugin&mod.title.up")}
                            <br />
                            {t("plugin&mod.title.down")} <span>{t("plugin&mod.title.bold")}</span>
                        </h1>
                        <p className="plugin-dev__description">
                            {t("plugin&mod.subtitle")}
                        </p>
                        <div className="plugin-dev__actions">
                            <a href={discordUrl} target="_blank" className="plugin-dev__button" rel="noreferrer">
                                {t("plugin&mod.preventive")}
                                <ArrowRight size={18} />
                            </a>
                        </div>
                    </div>

                    <div className="plugin-dev__presentation">
                        <PluginPresentationJava />
                    </div>
                </div>
            </div>

            {/* ===== STEP ===== */}
            <div className="plugin-dev__steps">
                <div className="plugin-dev__steps-container">
                    <header className="plugin-dev__steps-header">
                        <h2>{t("plugin&mod.develop.title")}</h2>
                        <p>{t("plugin&mod.develop.subtitle")}</p>
                    </header>

                    <div className="plugin-dev__steps-grid">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <div className="plugin-dev__step" key={step.id}>
                                    <div className="plugin-dev__step-icon">
                                        <Icon size={24} />
                                    </div>
                                    <h3 className="plugin-dev__step-title">{t(`plugin&mod.develop.card.${step.id}.title`)}</h3>
                                    <p className="plugin-dev__step-description">{t(`plugin&mod.develop.card.${step.id}.subtitle`)}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== TEAM ===== */}
            {pluginDevelopers.length > 0 && (
                <div className="plugin-dev__team">
                    <div className="plugin-dev__team-container">
                        <header className="plugin-dev__team-header">
                            <h2>{t("plugin&mod.employees.title")}</h2>
                            <p>{t("plugin&mod.employees.subtitle")}</p>
                        </header>

                        <div className="plugin-dev__team-grid">
                            {pluginDevelopers.map((dev) => (
                                <a
                                    key={dev.id}
                                    target="_blank"
                                    href={dev.avatar}
                                    rel="noopener noreferrer"
                                    className="plugin-dev__team-card"
                                >
                                    <div className="plugin-dev__team-avatar">
                                        <img src={dev.avatar} alt={dev.username} />
                                        <div className={`plugin-dev__team-status plugin-dev__team-status_${dev.online}`} />
                                    </div>
                                    <h3>{dev.username}</h3>
                                    <span>Plugin Developer</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}